/**
 * Контракты справочников значений списочных атрибутов (E04.5, ФТ-1.5.1).
 *
 * Список — именованный набор допустимых значений (тип ленты, условия эксплуатации).
 * Пополнение из клиентской формы — отдельно; здесь ведение справочника в админке.
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";

const nameSchema = z.string().trim().min(1).max(255);
const sortOrderSchema = z.number().int().min(0).max(100_000);
const itemValueSchema = z.string().trim().min(1).max(512);

/** Пункт списка значений. */
export const valueListItemSchema = z.object({
  value: itemValueSchema,
  sortOrder: sortOrderSchema,
});

export type ValueListItem = z.infer<typeof valueListItemSchema>;

/** Полный список значений для админки. */
export const valueListSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  items: z.array(valueListItemSchema),
  /** Дублирует `items.length` — удобно для выпадающих списков без разворота пунктов. */
  itemCount: z.number().int().nonnegative(),
  /** Сколько атрибутов ссылается на список — видно, что затронет удаление. */
  attributeCount: z.number().int().nonnegative(),
});

export type ValueList = z.infer<typeof valueListSchema>;

/** Тело `POST /api/v1/admin/value-lists`. */
export const createValueListRequestSchema = z.object({
  name: nameSchema,
  items: z.array(valueListItemSchema).default([]),
});

export type CreateValueListRequest = z.infer<typeof createValueListRequestSchema>;

/** Тело `PATCH /api/v1/admin/value-lists/{id}`. */
export const updateValueListRequestSchema = z
  .object({
    name: nameSchema.optional(),
    items: z.array(valueListItemSchema).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateValueListRequest = z.infer<typeof updateValueListRequestSchema>;

export const valueListListResponseSchema = apiSuccessSchema(z.array(valueListSchema));
export const valueListResponseSchema = apiSuccessSchema(valueListSchema);
